import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { WarningCircle, Envelope, Lock, ShieldCheck, Fingerprint, ChartLineUp } from '@phosphor-icons/react';
import CellRevealLogo from '../ui/CellRevealLogo';

const leftFeatures = [
  { icon: ShieldCheck, label: 'Peer-reviewed deconvolution methods' },
  { icon: Fingerprint, label: 'Versioned, citable signatures' },
  { icon: ChartLineUp, label: 'Publication-ready proportions' },
];

interface LoginLocationState {
  from?: { pathname: string };
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const from = (location.state as LoginLocationState | null)?.from?.pathname || '/jobs';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate(from, { replace: true });
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch {
      setError('Connection failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fillDemo = (demoEmail: string) => {
    setEmail(demoEmail);
    setPassword('demo123');
  };

  return (
    <div className="min-h-[100dvh] flex">

      {/* ── Left branding panel ───────────────────────────────────────────── */}
      <div className="hidden md:flex md:w-[44%] lg:w-[40%] flex-col justify-between bg-navy-950 relative overflow-hidden p-12 lg:p-16">
        {/* Grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(to right, rgb(94 234 212) 1px, transparent 1px), linear-gradient(to bottom, rgb(94 234 212) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            opacity: 0.04,
          }}
        />
        {/* Noise */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E\")",
            opacity: 0.03,
          }}
        />
        {/* Ambient glow */}
        <div className="absolute bottom-[-8%] right-[-8%] w-[400px] h-[400px] rounded-full blur-[120px] bg-teal-500/8 pointer-events-none" />
        <div className="absolute top-[-6%] left-[-6%] w-[300px] h-[300px] rounded-full blur-[100px] bg-teal-700/6 pointer-events-none" />

        {/* Logo */}
        <div className="relative">
          <CellRevealLogo variant="dark" size="md" />
        </div>

        {/* Middle: headline + features */}
        <div className="relative">
          <div className="mb-8">
            <p className="text-[1.6rem] font-display font-semibold text-white/88 leading-[1.2] tracking-tight">
              Context-matched signatures.
            </p>
            <p className="text-[1.6rem] font-display font-semibold leading-[1.2] tracking-tight text-teal-300">
              Reproducible results.
            </p>
          </div>

          <div className="space-y-3.5">
            {leftFeatures.map(({ icon: Icon, label }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-3"
              >
                <div className="w-7 h-7 rounded-lg bg-teal-900/35 border border-teal-700/30 flex items-center justify-center shrink-0">
                  <Icon className="w-3.5 h-3.5 text-teal-400" weight="duotone" />
                </div>
                <span className="text-teal-200/65 text-sm leading-snug">{label}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom: attribution */}
        <div className="relative flex items-center gap-3">
          <div className="h-px flex-1 bg-teal-400/10" />
          <p className="text-[10px] font-mono tracking-[0.35em] text-teal-400/28 uppercase shrink-0">
            Built on omnideconv
          </p>
        </div>
      </div>

      {/* ── Right: form panel ─────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-6 py-16 bg-background">
        <motion.div
          className="w-full max-w-[380px]"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Mobile: logo only */}
          <div className="md:hidden mb-10 flex justify-center">
            <CellRevealLogo variant="light" size="md" animated />
          </div>

          {/* Heading */}
          <div className="mb-8">
            <span className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase block mb-3">
              CellReveal
            </span>
            <h1 className="text-[1.75rem] font-bold tracking-tight text-foreground mb-2 leading-tight">
              Sign in
            </h1>
            <p className="text-muted-foreground text-sm">
              Access your deconvolution analyses
            </p>
          </div>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 p-3.5 mb-6 bg-red-50 border border-red-200/80 rounded-lg"
            >
              <WarningCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm leading-snug">{error}</p>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block mb-2 text-sm font-medium text-foreground">
                Email
              </label>
              <div className="relative">
                <Envelope className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 pointer-events-none" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@institution.edu"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-input rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 bg-background transition-colors duration-150"
                />
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-foreground">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 pointer-events-none" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-input rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 bg-background transition-colors duration-150"
                />
              </div>
            </div>

            <Button
              type="submit"
              variant="default"
              disabled={isLoading}
              className="w-full h-11 text-sm font-semibold"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                  Signing in…
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          {/* Demo accounts */}
          <div className="mt-8 pt-7 border-t border-border">
            <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/35 uppercase mb-4">
              Demo accounts
            </p>
            <div className="space-y-1">
              {[
                { email: 'sarah.chen@research.edu', tier: 'Professional' },
                { email: 'john.smith@lab.org', tier: 'Free' },
              ].map(acct => (
                <button
                  key={acct.email}
                  type="button"
                  onClick={() => fillDemo(acct.email)}
                  className="w-full flex items-center justify-between py-2.5 px-3 rounded-lg text-left hover:bg-muted/50 transition-colors duration-150 group"
                >
                  <div>
                    <p className="text-sm text-foreground group-hover:text-teal-700 transition-colors duration-150">{acct.email}</p>
                    <p className="text-xs font-mono text-muted-foreground/50 mt-0.5">password: demo123</p>
                  </div>
                  <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground/35 shrink-0 ml-4">
                    {acct.tier}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Back to home */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors duration-150"
            >
              ← Back to home
            </button>
          </div>
        </motion.div>
      </div>

    </div>
  );
}
